import React, { Fragment, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Form, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateSupplierMutation, useUpdateSupplierMutation, useGetSuppliersQuery } from '../../../../store/services/inventoryApi';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
    name: yup.string().required('Supplier Name is required').max(100),
    aliasName: yup.string().max(100).nullable().optional(),
    contactPersonName: yup.string().required('Contact Person Name is required').max(100),
    email: yup.string().email('Invalid email').required('Email is required'),
    phone: yup.string().required('Phone is required'),
    abn: yup.string().matches(/^[0-9]{11}$/, 'ABN must be exactly 11 digits').required('ABN is required'),
    status: yup.string().oneOf(['Active', 'Inactive']).default('Active'),
    address: yup.object().shape({
        addressLine1: yup.string().required('Address Line 1 is required').max(255),
        addressLine2: yup.string().max(255),
        city: yup.string().required('City is required').max(100),
        state: yup.string().oneOf(["VIC", "NSW", "QLD", "WA", "SA", "TAS", "NT", "ACT"], 'State is required').required(),
        postalCode: yup.string().required('Postal Code is required').max(10),
        country: yup.string().required('Country is required').default('AU'),
    })
});

const SupplierForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const { data: suppliersResult, isLoading: isFetching } = useGetSuppliersQuery(undefined, { skip: !isEdit });

    // Find supplier from the list
    const supplierData = React.useMemo(() => {
        if (!suppliersResult) return null;
        const list = Array.isArray(suppliersResult) ? suppliersResult : (suppliersResult.data || []);
        return list.find(s => s._id === id || s.id === id || s.supplierId === id);
    }, [suppliersResult, id]);
    const [createSupplier, { isLoading: isCreating }] = useCreateSupplierMutation();
    const [updateSupplier, { isLoading: isUpdating }] = useUpdateSupplierMutation();

    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            name: '',
            aliasName: '',
            contactPersonName: '',
            email: '',
            phone: '',
            abn: '',
            status: 'Active',
            address: {
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: 'VIC',
                postalCode: '',
                country: 'AU'
            }
        }
    });

    useEffect(() => {
        if (supplierData) {
            reset({
                ...supplierData,
                aliasName: supplierData.aliasName || '',
                contactPersonName: supplierData.contactName || supplierData.contactPersonName,
                phone: supplierData.contactPhone || supplierData.phone,
                email: supplierData.contactEmail || supplierData.email,
                status: supplierData.status || 'Active',
                address: {
                    ...supplierData.address,
                    addressLine1: supplierData.address?.street || supplierData.address?.addressLine1,
                    country: supplierData.address?.country || 'AU'
                }
            });
        }
    }, [supplierData, reset]);

    const onSubmit = async (data) => {
        const payload = {
            name: data.name,
            aliasName: data.aliasName || '',
            abn: data.abn,
            contactName: data.contactPersonName,
            contactPhone: data.phone,
            contactEmail: data.email,
            status: data.status,
            address: {
                street: data.address.addressLine1 + (data.address.addressLine2 ? ', ' + data.address.addressLine2 : ''),
                city: data.address.city,
                state: data.address.state,
                postalCode: data.address.postalCode,
                country: data.address.country
            }
        };

        try {
            if (isEdit) {
                await updateSupplier({ id, ...payload }).unwrap();
                toast.success('Supplier updated successfully');
            } else {
                await createSupplier(payload).unwrap();
                toast.success('Supplier created successfully');
            }
            navigate('/inventory/suppliers');
        } catch (err) {
            console.error("Supplier submit error:", err);
            toast.error(err?.data?.message || err?.error || 'Something went wrong');
        }
    };

    if (isFetching) return <div className="p-5 text-center">Loading supplier data...</div>;

    return (
        <Fragment>
            <Breadcrumbs mainTitle={isEdit ? "Edit Supplier" : "Add Supplier"} parent="Inventory" title={isEdit ? "Edit Supplier" : "Add Supplier"} />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>{isEdit ? `Edit Supplier: ${supplierData?.name}` : "Supplier Information"}</h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                     <Row>
                                         <Col md="4">
                                             <FormGroup>
                                                 <Label for="name">Supplier Name</Label>
                                                 <Controller
                                                     name="name"
                                                     control={control}
                                                     render={({ field }) => (
                                                         <Input {...field} type="text" id="name" invalid={!!errors.name} placeholder="Business Name" />
                                                     )}
                                                 />
                                                 <FormFeedback>{errors.name?.message}</FormFeedback>
                                             </FormGroup>
                                         </Col>
                                         <Col md="4">
                                             <FormGroup>
                                                 <Label for="aliasName">Alias Name</Label>
                                                 <Controller
                                                     name="aliasName"
                                                     control={control}
                                                     render={({ field }) => (
                                                         <Input {...field} type="text" id="aliasName" invalid={!!errors.aliasName} placeholder="Short / Alternative Name" />
                                                     )}
                                                 />
                                                 <FormFeedback>{errors.aliasName?.message}</FormFeedback>
                                             </FormGroup>
                                         </Col>
                                         <Col md="4">
                                             <FormGroup>
                                                 <Label for="contactPersonName">Contact Person</Label>
                                                 <Controller
                                                     name="contactPersonName"
                                                     control={control}
                                                     render={({ field }) => (
                                                         <Input {...field} type="text" id="contactPersonName" invalid={!!errors.contactPersonName} placeholder="Full Name" />
                                                     )}
                                                 />
                                                 <FormFeedback>{errors.contactPersonName?.message}</FormFeedback>
                                             </FormGroup>
                                         </Col>
                                     </Row>

                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label for="email">Email</Label>
                                                <Controller
                                                    name="email"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="email" id="email" invalid={!!errors.email} placeholder="email@example.com" />
                                                    )}
                                                />
                                                <FormFeedback>{errors.email?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label for="phone">Phone</Label>
                                                <Controller
                                                    name="phone"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="phone" invalid={!!errors.phone} placeholder="+61..." />
                                                    )}
                                                />
                                                <FormFeedback>{errors.phone?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label for="abn">ABN</Label>
                                                <Controller
                                                    name="abn"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="abn" invalid={!!errors.abn} placeholder="11 digit ABN" />
                                                    )}
                                                />
                                                <FormFeedback>{errors.abn?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="4">
                                            <FormGroup>
                                                <Label for="status">Status</Label>
                                                <Controller
                                                    name="status"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="select" id="status" invalid={!!errors.status}>
                                                            <option value="Active">Active</option>
                                                            <option value="Inactive">Inactive</option>
                                                        </Input>
                                                    )}
                                                />
                                                <FormFeedback>{errors.status?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <h6 className="mt-4">Address Information</h6>
                                    <hr />
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="addressLine1">Address Line 1</Label>
                                                <Controller
                                                    name="address.addressLine1"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="addressLine1" invalid={!!errors.address?.addressLine1} placeholder="Street Address" />
                                                    )}
                                                />
                                                <FormFeedback>{errors.address?.addressLine1?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="addressLine2">Address Line 2 (Optional)</Label>
                                                <Controller
                                                    name="address.addressLine2"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="addressLine2" invalid={!!errors.address?.addressLine2} placeholder="Suite/Apt" />
                                                    )}
                                                />
                                                <FormFeedback>{errors.address?.addressLine2?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="city">City</Label>
                                                <Controller
                                                    name="address.city"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="city" invalid={!!errors.address?.city} placeholder="City/Suburb" />
                                                    )}
                                                />
                                                <FormFeedback>{errors.address?.city?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="state">State</Label>
                                                <Controller
                                                    name="address.state"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="select" id="state" invalid={!!errors.address?.state}>
                                                            {["VIC", "NSW", "QLD", "WA", "SA", "TAS", "NT", "ACT"].map(s => (
                                                                <option key={s} value={s}>{s}</option>
                                                            ))}
                                                        </Input>
                                                    )}
                                                />
                                                <FormFeedback>{errors.address?.state?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="postalCode">Postal Code</Label>
                                                <Controller
                                                    name="address.postalCode"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="postalCode" invalid={!!errors.address?.postalCode} placeholder="Postcode" />
                                                    )}
                                                />
                                                <FormFeedback>{errors.address?.postalCode?.message}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md="3">
                                            <FormGroup>
                                                <Label for="country">Country</Label>
                                                <Controller
                                                    name="address.country"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input {...field} type="text" id="country" disabled value="AU" />
                                                    )}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="text-end mt-4">
                                        <Button color="light" className="me-2" onClick={() => navigate('/inventory/suppliers')} disabled={isCreating || isUpdating}>
                                            Cancel
                                        </Button>
                                        <Button color="primary" type="submit" disabled={isCreating || isUpdating}>
                                            {isCreating || isUpdating ? 'Saving...' : (isEdit ? 'Update Supplier' : 'Create Supplier')}
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default SupplierForm;
